import { AllCompaniesMembersResponse } from 'api/company'
import { PropsWithChildren, useCallback, useMemo, useState } from 'react'
import { createContext } from 'lib/createContext'
import { useUpdateCompanyGroupMemberCompanies } from 'queries/company'
import { toast } from '@workwhile/ui'
import { useCompanyMembersContext } from '../../../context/CompanyMembersProvider'
import { useAuthContext } from 'components/auth'
import type { AddRemoveDisabledReason } from '../addRemoveDisabled'
import { getCompaniesInCompanyGroup } from 'lib/companies'

const COMPANY_GROUP_ADMIN_ROLE = 'Company group administrator'

type EditMemberProviderContextState = {
  state: {
    member: AllCompaniesMembersResponse['members'][number] | undefined
    isLoadingMember: boolean
    companyOptions: { value: number; label: string }[]
    isPending: boolean
    selectedCompanyId: number | undefined
    addRemoveDisabledReason: AddRemoveDisabledReason | null
  }
  actions: {
    addCompany: (companyId: number) => void
    removeCompany: (companyId: number) => void
    setSelectedCompanyId: (companyId: number) => void
  }
}

export const [EditMemberContext, useEditMemberContext] =
  createContext<EditMemberProviderContextState>({
    name: 'EditMemberContext',
  })

export const EditMemberProvider = ({
  children,
  memberId,
}: PropsWithChildren<{
  memberId: number
}>) => {
  const { user, company } = useAuthContext()
  const {
    state: { membersPage, isLoading: isLoadingMember },
    actions: { refetch: refetchCompanyMembers, setSelectedMemberId },
  } = useCompanyMembersContext()
  const { mutate: updateCompanyGroupMemberCompanies, isPending } =
    useUpdateCompanyGroupMemberCompanies()
  const [selectedCompanyId, setSelectedCompanyId] = useState<
    number | undefined
  >(undefined)

  const member = useMemo(() => {
    return membersPage.find((m) => m.id === memberId)
  }, [membersPage, memberId])

  const addRemoveDisabledReason =
    useMemo((): AddRemoveDisabledReason | null => {
      if (!member) {
        return null
      }
      const isViewerSelf =
        !!member.email &&
        !!user?.email &&
        member.email.toLowerCase() === user.email.toLowerCase()
      if (isViewerSelf) {
        return 'viewerSelf'
      }
      const isCompanyGroupAdmin =
        member.companyRolePermission?.description === COMPANY_GROUP_ADMIN_ROLE
      return isCompanyGroupAdmin ? 'companyGroupAdmin' : null
    }, [member, user?.email])

  const handleAddCompanyAccess = useCallback(
    (companyId: number) => {
      if (!member) {
        return
      }
      updateCompanyGroupMemberCompanies(
        {
          userId: member.id,
          addCompanyIds: [companyId],
          removeCompanyIds: [],
        },
        {
          onSuccess: () => {
            setSelectedCompanyId(undefined)
            toast.success('Successfully added company access.')
            refetchCompanyMembers()
          },
          onError: (err) => {
            toast.error((err as Error).message)
          },
        }
      )
    },
    [
      member,
      updateCompanyGroupMemberCompanies,
      setSelectedCompanyId,
      refetchCompanyMembers,
    ]
  )

  const handleRemoveCompanyAccess = useCallback(
    (companyId: number) => {
      if (!member) {
        return
      }
      const isMemberLastCompany = member.companies.length <= 1

      updateCompanyGroupMemberCompanies(
        {
          userId: member.id,
          addCompanyIds: [],
          removeCompanyIds: [companyId],
        },
        {
          onSuccess: () => {
            toast.success('Successfully removed company access.')
            if (isMemberLastCompany) {
              setSelectedMemberId(undefined)
            }
            refetchCompanyMembers()
          },
          onError: (err) => {
            toast.error((err as Error).message)
          },
        }
      )
    },
    [
      member,
      updateCompanyGroupMemberCompanies,
      refetchCompanyMembers,
      setSelectedMemberId,
    ]
  )

  const companyOptions = useMemo(() => {
    if (member && company) {
      const companies = getCompaniesInCompanyGroup(company)
      const memberOfCompanyIds = member.companies.map((c) => c.id)
      return companies
        .filter((c) => !memberOfCompanyIds.includes(Number(c.id)))
        .map((c) => ({
          value: Number(c.id),
          label: c.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    }

    return []
  }, [company, member])

  const value = useMemo(() => {
    return {
      state: {
        member,
        isLoadingMember,
        companyOptions,
        isPending,
        selectedCompanyId,
        addRemoveDisabledReason,
      },
      actions: {
        addCompany: handleAddCompanyAccess,
        removeCompany: handleRemoveCompanyAccess,
        setSelectedCompanyId,
      },
    }
  }, [
    member,
    isLoadingMember,
    companyOptions,
    isPending,
    addRemoveDisabledReason,
    handleAddCompanyAccess,
    handleRemoveCompanyAccess,
    selectedCompanyId,
    setSelectedCompanyId,
  ])

  return (
    <EditMemberContext.Provider value={value}>
      {children}
    </EditMemberContext.Provider>
  )
}
